import React, { useEffect, useState } from "react";
import PageLoader from "../../../shared/PageLoader";
import { useAppDispatch, useAppSelector } from "../../../../redux/hook";

import Select from "react-select";
import PerformerDropdownIndicator from "../shared/PerformerDropdownIndicator";
import Modal from "react-bootstrap/Modal";

import { IoMdClose } from "react-icons/io";
import Icon from "../../../common/Icon";
import { downloadWithPresignedUrl } from "../../../../utils/common";
import {
  ERROR_MESSAGES,
  ESTABLISHMENT_TYPE,
} from "../../../../utils/constants";
import { toast } from "react-toastify";
import { hasUserAccess } from "../../../../backend/auth";
import { USER_PRIVILEGES } from "../../UserManagement/Roles/RoleConfiguration";
import { addStateRegisterQue } from "../../../../redux/features/stateRegisterQue.slice";

import {
  useFilters,
  useSearchConfig,
  useStateRegisterData,
  useCompanyData,
  useEmployerData,
} from "../../../../hooks/usePerformerClientRegisters";
import {
  formatDate,
  generateYearsList,
  MONTH_LIST,
  getResetFields,
} from "../../../../utils/performerClientRegisters.utils";

import PerformerTable from "../shared/PerformerTable";
import EmployeeUploadSearch from "../../../common/EmployeeUploadSearch";
import DownloadModal from "./DownloadModal";
import "../shared/performer-client.css";


const RegistersQueue = () => {
  const dispatch: any = useAppDispatch();
  const stateRegisterQueDetails = useAppSelector(
    (state) => state.stateRegisterQue.stateRegisterQueDetails,
  );
  const stateRegisterQueExcelandPdfDetails = useAppSelector(
    (state) =>
      state.stateRegisterQue.stateRegisterQueExcelandPdfDownloadDetails,
  );

  const QUE_STATUS_DISPLAY: Record<string, string> = {
    pending: 'Pending',
    processing: 'In Progress',
    excel_processing: 'In Progress',
    pdf_processing: 'In Progress',
    pdf_processing_partially_completed: 'Failed',
    pdf_processing_completed: 'Completed',
    failed: 'Failed'
  };


  // Use custom hooks
  const {
    filters,
    updateFilter,
    updateMultipleFilters,
    resetFilters,
    setFilters,
  } = useFilters();
  const { searchConfig, updateSearchConfig } = useSearchConfig();
  const { fetchData, fetchWithSearch } = useStateRegisterData(
    filters,
    searchConfig,
  );
  const {
    companies,
    associateCompanies,
    locations,
    totalStates,
    fetchCompanies,
    fetchAssociateCompanies,
    fetchLocations,
    fetchStates,
  } = useCompanyData();
  const {
    employers,
    employerLocations,
    employerSubContractors,
    employerSubContractorsLocations,
    fetchEmployers,
    fetchEmployerLocations,
    fetchSubContractors,
    fetchSubContractorLocations,
  } = useEmployerData();

  // Extract states from locations
  let states =
    (locations || [])
      .filter((each: any) => each.state)
      .map((each: any) => {
        return { name: each.state.name, id: each.state.id };
      })
      .filter((each: any, index: any, self: any) => {
        return index === self.findIndex((t: any) => t.id === each.id);
      })
      .sort((a: any, b: any) => (a.name || "").localeCompare(b.name || "")) || [];

  // Local state for UI components
  const [downloading, setDownloading] = useState(false);
  const [openDownloadModal, setOpenDownloadModal] = useState(false);
  const [selectedStateRegisterQue, setSelectedStateRegisterQue] = useState<any>(
    {},
  );
  const [stateRegisters, setStateRegisters] = useState<any[]>([]);
  const [showDashboardDetails, setShowDashboardDetails] = useState(true);
  const [queueNumbers, setQueueNumbers] = useState<string[]>([]);
  const [openMoreFilters, setOpenMoreFilters] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);


  // Redux selectors
  const formsDetails = useAppSelector(
    (state) => state.performerClient.formsDetails,
  );
  const addStateRegisterDetails = useAppSelector(
    (state) => state.stateRegister.addStateRegisterDetails,
  );
  const updateStateRegisterDetails = useAppSelector(
    (state) => state.stateRegister.updateStateRegisterDetails,
  );
  const deleteStateRegisterMappingDetails = useAppSelector(
    (state) => state.stateRegister.deleteStateRegisterMappingDetails,
  );
  const importStateRegisterMappingDetails = useAppSelector(
    (state) => state.stateRegister.importStateRegisterMappingDetails,
  );

  // Data from selectors
  const stateRegisterQue = stateRegisterQueDetails.data.items;
  const stateRegisterQueCount = stateRegisterQueDetails.data.meta.total_count;

  // Check if CLRA columns should be shown (CLRA filter selected OR search active)
  const showCLRAColumns =
    filters.establishmentType === "CLRA" || isSearchActive;

  // Loading state
  const loading =
    importStateRegisterMappingDetails.status === "loading" ||
    deleteStateRegisterMappingDetails.status === "loading" ||
    updateStateRegisterDetails.status === "loading" ||
    addStateRegisterDetails.status === "loading" ||
    formsDetails.status === "loading" ||
    stateRegisterQueDetails.status === "loading";

  const handleChangeCompany = (option: any) => {
    const selectedCompanyId = option ? option.value : "";
    updateMultipleFilters({
      company: selectedCompanyId,
      associateCompany: "",
      stateName: "",
      locations: [],
      employer: "",
      employerLocation: "",
      subContractor: "",
      subContractorLocation: "",
    });
    updateSearchConfig({ page: 0 });

    fetchData({
      ...filters,
      company: selectedCompanyId,
      associateCompany: "",
      stateName: "",
      locations: [],
      employer: "",
      employerLocation: "",
      subContractor: "",
      subContractorLocation: "",
    }, { page: 0 }, {
      que_id_eq: searchConfig.searchInput,
    });

    if (selectedCompanyId) {
      fetchAssociateCompanies(selectedCompanyId);
    }
  };

  // Removed: Using generic getNameById from utils instead

  const handleChangeAssociateCompany = (option: any) => {
    const selectedCompanyId = option ? option.value : "";
    updateMultipleFilters({
      associateCompany: selectedCompanyId,
      stateName: "",
      locations: [],
      employer: "",
      employerLocation: "",
      subContractor: "",
      subContractorLocation: "",
    });
    updateSearchConfig({ page: 0 });

    fetchData({
      ...filters,
      associateCompany: selectedCompanyId,
      stateName: "",
      locations: [],
      employer: "",
      employerLocation: "",
      subContractor: "",
      subContractorLocation: "",
    }, { page: 0 });

    if (selectedCompanyId) {
      fetchLocations(selectedCompanyId);
      fetchStates();
    }
  };

  // Removed: Using generic getNameById from utils instead

  const handleChangeStateName = (option: any) => {
    const selectedStateId = option ? option.value : "";
    updateMultipleFilters({ stateName: selectedStateId, locations: [], year: "", month: "" });
    updateSearchConfig({ page: 0 });
    fetchData({ ...filters, stateName: selectedStateId, locations: [], year: "", month: "" }, { page: 0 });
  };

  const handleChangeLocationMulti = (options: any) => {
    const selectedLocations = (options || []).map((o: any) => o.value);
    updateFilter("locations", selectedLocations);
    updateSearchConfig({ page: 0 });
    fetchData({ ...filters, locations: selectedLocations }, { page: 0 });
    if (selectedLocations && selectedLocations.length > 0) {
      fetchEmployers({ ...filters, locations: selectedLocations }, locations);
    }
  };

  const handleChangeLocationSingle = (option: any) => {
    const selectedLocations = option && option.value ? [option.value] : [];
    updateMultipleFilters({
      locations: selectedLocations,
      employer: "",
      employerLocation: "",
      subContractor: "",
      subContractorLocation: "",
    });
    updateSearchConfig({ page: 0 });
    fetchData({
      ...filters,
      locations: selectedLocations,
      employer: "",
      employerLocation: "",
      subContractor: "",
      subContractorLocation: "",
    }, { page: 0 });
    if (selectedLocations && selectedLocations.length > 0) {
      fetchEmployers({ ...filters, locations: selectedLocations }, locations);
    }
  };

  const handleChangeYear = (option: any) => {
    const selectedYear = option ? option.value : "";
    updateFilter("year", selectedYear);
    updateSearchConfig({ page: 0 });

    fetchData({ ...filters, year: selectedYear, month: "" }, { page: 0 });
  };

  const handleChangeMonth = (option: any) => {
    const selectedMonth = option ? option.value : "";
    updateFilter("month", selectedMonth);
    updateSearchConfig({ page: 0 });

    fetchData({ ...filters, month: selectedMonth }, { page: 0 });
  };

  const handleChangeEstablishmentType = (option: any) => {
    const selectedType = option ? option.value : "";
    setIsSearchActive(false);
    updateFilter("establishmentType", selectedType);
    updateSearchConfig({ page: 0 });
    const resetFields = getResetFields("establishmentType");
    const cleanFilters = {
      company: "",
      associateCompany: "",
      stateName: "",
      locations: [],
      year: "",
      month: "",
      employer: "",
      employerLocation: "",
      subContractor: "",
      subContractorLocation: "",
      ...resetFields,
      establishmentType: selectedType,
    };

    fetchData(cleanFilters, { page: 0 }, { que_id_eq: searchConfig.searchInput });
  };

  // Initialize data on component mount
  useEffect(() => {
    fetchCompanies();
    const params = new URLSearchParams(window.location.search);
    const queId = params.get("queId");
    if (queId) {
      updateSearchConfig({ searchInput: queId });
      setIsSearchActive(true);
      fetchWithSearch(queId);
    } else {
      fetchData();
    }
  }, []);

  // Fetch associate companies when company changes
  useEffect(() => {
    if (filters.company) {
      fetchAssociateCompanies(filters.company);
    }
  }, [filters.company]);

  // Fetch locations when associate company changes
  useEffect(() => {
    if (filters.associateCompany) {
      fetchLocations(filters.associateCompany);
      fetchStates();
    }
  }, [filters.associateCompany]);

  // Removed: Consolidated with main useEffect above

  // Removed: Consolidated with filters.company useEffect

  // Removed: Consolidated with filters.associateCompany useEffect

  useEffect(() => {
    if (stateRegisterQueDetails.status === "succeeded") {
      //alert("this success  was hitted");
    } else if (stateRegisterQueDetails.status === "failed") {
      toast.error(ERROR_MESSAGES.DEFAULT);
    }
  }, [stateRegisterQueDetails.status]);

  // Fetch employer-related data when filters change
  useEffect(() => {
    if (filters.locations && filters.locations.length > 0) {
      fetchEmployers(filters, locations);
    }
  }, [filters.locations]);

  useEffect(() => {
    if (filters.employer) {
      fetchEmployerLocations(filters, locations);
    }
  }, [filters.employer]);

  useEffect(() => {
    if (filters.employerLocation) {
      fetchSubContractors(filters, locations);
    }
  }, [filters.employerLocation]);

  useEffect(() => {
    if (filters.subContractor) {
      fetchSubContractorLocations(filters, locations);
    }
  }, [filters.subContractor]);

  function handleChangeEmployer(option: any) {
    const selectedEmployer = option ? option.value : "";
    updateMultipleFilters({
      employer: selectedEmployer,
      employerLocation: "",
      subContractor: "",
      subContractorLocation: "",
    });
    updateSearchConfig({ page: 0 });

    fetchData({
      ...filters,
      employer: selectedEmployer,
      employerLocation: "",
      subContractor: "",
      subContractorLocation: "",
    }, { page: 0 });

    if (selectedEmployer) {
      fetchEmployerLocations(
        { ...filters, employer: selectedEmployer },
        locations,
      );
    }
  }

  function handleChangeEmployerLocation(option: any) {
    const selectedLocation = option ? option.value : "";
    updateMultipleFilters({
      employerLocation: selectedLocation,
      subContractor: "",
      subContractorLocation: "",
    });
    updateSearchConfig({ page: 0 });

    fetchData({
      ...filters,
      employerLocation: selectedLocation,
      subContractor: "",
      subContractorLocation: "",
    }, { page: 0 });

    if (selectedLocation) {
      fetchSubContractors(
        { ...filters, employerLocation: selectedLocation },
        locations,
      );
    }
  }

  function handleChangeSubContractor(event: any) {
    const selectedSubContractor = event.value as string;
    updateFilter("subContractor", selectedSubContractor);

    if (selectedSubContractor) {
      fetchSubContractorLocations(
        { ...filters, subContractor: selectedSubContractor },
        locations,
      );
    }
  }

  function handleChangeSubContractorLocation(event: any) {
    const selectedLocation = event.value as string;
    updateFilter("subContractorLocation", selectedLocation);
  }

  function applyMoreFilters() {
    setOpenMoreFilters(false);
    updateSearchConfig({ page: 0 });
    fetchData(undefined, { page: 0 });
  }

  function clearMoreFilters() {
    updateMultipleFilters({
      subContractor: "",
      subContractorLocation: "",
    });
    setTimeout(() => {
      setOpenMoreFilters(false);
      updateSearchConfig({ page: 0 });
      fetchData({ ...filters, subContractor: "", subContractorLocation: "" }, { page: 0 });
    }, 100);
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    updateSearchConfig({ page: newPage });
    fetchData(undefined, { page: newPage });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    updateSearchConfig({ rowsPerPage: newRowsPerPage, page: 0 });
    fetchData(undefined, { rowsPerPage: newRowsPerPage, page: 0 });
  };

  const handleSort = (sortKey: string) => {
    const newSortType = searchConfig.activeSort === sortKey && searchConfig.sortType === "asc" ? "desc" : "asc";
    updateSearchConfig({ activeSort: sortKey, sortType: newSortType });
    fetchData(filters, undefined, {}, sortKey, newSortType);
  };

  // Use utility functions for lists
  const yearsList = generateYearsList();
  const monthList = MONTH_LIST;
  const EstablishmentTypesList = Object.values(ESTABLISHMENT_TYPE);

  // react-select options
  const ALL_OPTION = { value: '', label: 'All' };
  const establishmentTypeOptions = [ALL_OPTION, ...EstablishmentTypesList.map((each) => ({ value: each, label: each }))];
  const companyOptions = [ALL_OPTION, ...companies.map((c: any) => ({ value: c.id, label: c.name }))];
  const associateCompanyOptions = [ALL_OPTION, ...associateCompanies.map((c: any) => ({ value: c.id, label: c.name }))];
  const stateOptions = [ALL_OPTION, ...states.map((s: any) => ({ value: s.id, label: s.name }))];
  const sortLocationsByStateAndName = (a: any, b: any) => {
    const stateCmp = ((a.state && a.state.name) || "").localeCompare((b.state && b.state.name) || "");
    if (stateCmp !== 0) return stateCmp;
    return (a.name || "").localeCompare(b.name || "");
  };
  const nonClraLocationOptions = (locations || [])
    .filter((each: any) => {
      return each.location_id && each.state && each.state.id === filters.stateName;
    })
    .slice()
    .sort(sortLocationsByStateAndName)
    .map((each: any) => {
      const { name, code, city, state } = each;
      return { value: each.location_id, label: `${name} (${state.code}-${(city || {}).code}-${code})` };
    });
  const clraLocationOptions = (locations || [])
    .filter((each: any) => {
      return each.location_id && each.state;
    })
    .slice()
    .sort(sortLocationsByStateAndName)
    .map((each: any) => {
      const { name, code, city, state } = each;
      return { value: each.location_id, label: `${name} (${state.code}-${(city || {}).code}-${code})` };
    });
  const employerOptions = [ALL_OPTION, ...(employers || []).map((e: any) => ({ value: e.id, label: e.name }))];
  const employerLocationOptions = [ALL_OPTION, ...(employerLocations || []).map((e: any) => ({ value: e.id, label: e.location_name }))];
  const yearOptions = [ALL_OPTION, ...yearsList.map((y: any) => ({ value: y, label: String(y) }))];
  const monthOptions = [ALL_OPTION, ...monthList.map((m: any) => ({ value: m, label: m }))];

  // react-select selected values
  const selectedEstablishmentType = filters.establishmentType ? { value: filters.establishmentType, label: filters.establishmentType } : ALL_OPTION;
  const selectedCompany = (filters.company && companyOptions.find((o: any) => o.value === filters.company)) || ALL_OPTION;
  const selectedAssociateCompany = (filters.associateCompany && associateCompanyOptions.find((o: any) => o.value === filters.associateCompany)) || ALL_OPTION;
  const selectedStateName = (filters.stateName && stateOptions.find((o: any) => o.value === filters.stateName)) || ALL_OPTION;
  const selectedLocationsMulti = filters.locations.map((id: string) => nonClraLocationOptions.find((o: any) => o.value === id)).filter(Boolean);
  const selectedLocationSingle = (filters.locations.length > 0 && clraLocationOptions.find((o: any) => o.value === filters.locations[0])) || ALL_OPTION;
  const selectedEmployer = (filters.employer && employerOptions.find((o) => o.value === filters.employer)) || ALL_OPTION;
  const selectedEmployerLocation = (filters.employerLocation && employerLocationOptions.find((o) => o.value === filters.employerLocation)) || ALL_OPTION;
  const selectedYear = filters.year ? { value: filters.year, label: String(filters.year) } : ALL_OPTION;
  const selectedMonth = filters.month ? { value: filters.month, label: filters.month } : ALL_OPTION;

  const handleProcessRegisters = async () => {
    setDownloading(true);
    let newQueueNumbers: string[] = [];
    try {
      for (const location of filters.locations) {
        let payload: any = {};
        switch (filters.establishmentType) {
          case "CLRA":
            const loc = locations.find((l: any) => l.location_id === location);
            payload = {
              state_register_que: {
                company_id: filters.company,
                associate_company_id: filters.associateCompany,
                location_id: loc ? loc.location_id : undefined,
                company_location_mapping_id: loc ? loc.company_location_mapping_id : undefined,
                state_id:
                  loc && loc.state
                    ? loc.state.id
                    : undefined,
                year: filters.year,
                month: filters.month,
                establishment_type: filters.establishmentType,
              },
            };
            if (filters.employer) {
              payload.state_register_que["employer_id"] = filters.employer;
            }
            if (filters.employerLocation) {
              payload.state_register_que["employer_location_id"] =
                filters.employerLocation;
            }
            if (filters.subContractor) {
              payload.state_register_que["sub_contractor_id"] =
                filters.subContractor;
            }
            if (filters.subContractorLocation) {
              payload.state_register_que["sub_contractor_location_id"] =
                filters.subContractorLocation;
            }
            break;
          case "Shops":
            payload = {
              state_register_que: {
                company_id: filters.company,
                associate_company_id: filters.associateCompany,
                location_id: location,
                state_id: filters.stateName,
                year: filters.year,
                month: filters.month,
                establishment_type: filters.establishmentType,
              },
            };
            break;
          default:
            payload = {
              state_register_que: {
                company_id: filters.company,
                associate_company_id: filters.associateCompany,
                location_id: location,
                state_id: filters.stateName,
                year: filters.year,
                month: filters.month,
                establishment_type: filters.establishmentType,
              },
            };
            break;
        }
        const resultAction = await dispatch(
          addStateRegisterQue({
            payload,
            establishmentType: filters.establishmentType,
          }),
        );
        if (addStateRegisterQue.fulfilled.match(resultAction)) {
          if (filters.establishmentType === "CLRA") {
            const queList = resultAction.payload.data.state_register_ques;
            const skipped_list = resultAction.payload.data.skipped_employers || [];
            if (queList && Array.isArray(queList)) {
              const queIds = queList
                .map((item: any) => item.que_id)
                .filter(Boolean);
              newQueueNumbers.push(...queIds);
            } else {
              const queId = resultAction.payload.data.que_id;
              if (queId) {
                newQueueNumbers.push(queId);
              }
            }
            if (skipped_list.length > 0) {                                                                                                                                                                             
              toast.warn(`${skipped_list.length} record(s) were skipped due to missing data (no employees/wage/attendance data found).`);
            }
          } else {
            const queId = resultAction.payload.data.que_id;
            if (queId) {
              newQueueNumbers.push(queId);
            }
          }
        } else {
          const errorData = resultAction.payload || {};
          const skipped_list = (errorData as any).skipped_employers || [];                                                                                                                                             
          if (skipped_list.length > 0) {
            toast.warn(`${skipped_list.length} record(s) were skipped due to missing data.`);                                                                                                                          
          } 
          if (Array.isArray(errorData.errors) && errorData.errors.length > 0) {
            toast.error(
              <div>
                {errorData.errors.map((err: string, i: number) => (
                  <div key={i}>{err}</div>
                ))}
              </div>
            );
          } else if (errorData.error) {
            toast.error(errorData.error);
          } else {
            toast.error("An error occurred while processing the registers.");
          }
          setDownloading(false);
          return;
        }
      }
      // Update the state with the new queue numbers
      setQueueNumbers((prevQueueNumbers) => [
        ...prevQueueNumbers,
        ...newQueueNumbers,
      ]);
      // Step 3: Display the QueueNo list to the user in a popup
      if (newQueueNumbers.length > 0) {
        toast.success(`Queue Numbers received : \n${newQueueNumbers.join(", ")}`);
        fetchData();
      } else {
        toast.error("No Queue Numbers found.");
      }
      setDownloading(false);
    } catch (error) {
      console.error("Error processing registers:", error);
      toast.error("An error occurred while processing the registers.");
      setDownloading(false);
    }
  };

  // Moved interfaces to utils if needed globally

  const onClickDownloadZip = (url: any) => {
    if (url) {
      downloadWithPresignedUrl(url.split("/").pop(), url);
    } else {
      toast.info("No URL found");
    }
  };

  const onclickDownloadExcel = (file: any) => {
    const url = file.register_excel_url || "";
    if (url) {
      downloadWithPresignedUrl(url.split("/").pop(), url);
    } else {
      toast.info("No URL found");
      return "No URL found";
    }
  };

  const onclickDownloadPdf = (file: any) => {
    const url = file.register_pdf_url || "";
    if (url) {
      downloadWithPresignedUrl(url.split("/").pop(), url);
    } else {
      toast.info("No URL found");
      return "No URL found";
    }
  };

  const onclickDownloadBoth = (each: any) => {
    setOpenDownloadModal(true);
    //dispatch(getstateRegisterQueExcelandPdfDownloadDetails(Payload))
    setSelectedStateRegisterQue(each);
    const _stateRegisters = each.state_registers || [];
    const updatedStateRegisters: any = [];
    _stateRegisters.forEach((_stateRegister: any) => {
      const _registers = [...(_stateRegister.registers || [])].sort(
        (a: any, b: any) => Number(a.id) - Number(b.id),
      );
      if (_registers.length > 1) {
        updatedStateRegisters.push(
          ..._registers.map((register: any, index: number) => {
            return {
              ..._stateRegister,
              form_name: _stateRegister.form_name + ` Part - ${index + 1}`,
              register_excel_url: register.excel_url || "",
              register_pdf_url: register.pdf_url || "",
            };
          }),
        );
      } else {
        updatedStateRegisters.push({
          ..._stateRegister,
          register_excel_url: (_registers[0] || {}).excel_url || "",
          register_pdf_url: (_registers[0] || {}).pdf_url || "",
        });
      }
    });
    setStateRegisters(updatedStateRegisters);
  };

  // Using formatDate from utils

  const handleChangeSearchInput = (event: any) => {
    updateSearchConfig({ searchInput: event.target.value });
  };

  const onClickClearSearch = () => {
    setIsSearchActive(false);
    updateSearchConfig({ searchInput: "" });
    fetchData();
  };

  const onClickSearch = () => {
    setIsSearchActive(true);
    updateSearchConfig({ page: 0 });
    fetchWithSearch(searchConfig.searchInput);
  };

  const subContractorOptions = employerSubContractors.map((x: any) => ({ label: x.name, value: x.id }));
  const subContractorLocationOptions = employerSubContractorsLocations.map((x: any) => ({ label: x.location_name, value: x.id }));
  const selectedSubContractor = subContractorOptions.find((o) => o.value === filters.subContractor) || null;
  const selectedSubContractorLocation = subContractorLocationOptions.find((o) => o.value === filters.subContractorLocation) || null;

  return (
    <>
    <div className="performerclient-wrapper h-100">
      <div className="performer-client-page h-100 py-3">
      {/* Download Modal */}
      <DownloadModal
        open={openDownloadModal}
        onClose={() => setOpenDownloadModal(false)}
        stateRegisters={stateRegisters}
        selectedStateRegisterQue={selectedStateRegisterQue}
        onclickDownloadExcel={onclickDownloadExcel}
        onclickDownloadPdf={onclickDownloadPdf}
        onClickDownloadZip={onClickDownloadZip}
      />
      <div>
        <div>
              <div className="pageHeading mb-0" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h4 className="mb-0">Register Generation</h4>
                <EmployeeUploadSearch
                  value={searchConfig.searchInput}
                  placeholder="Search Que No"
                  onChange={handleChangeSearchInput}
                  onSearch={onClickSearch}
                  onClear={onClickClearSearch}
                />
              </div>
              {/* Filter Box*/}
              <div className="filters" style={{ padding: "16px 20px" }}>
                <div
                  style={{
                    backgroundColor: "#ffffff",
                    padding: "16px 16px",
                    borderRadius: "8px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "24px",
                      alignItems: "flex-end",
                    }}
                  >
                    <div style={{ minWidth: "250px" }}>
                      <label className="filter-label"><small>Establishment Type</small></label>
                      <Select
                        options={establishmentTypeOptions}
                        value={selectedEstablishmentType}
                        onChange={handleChangeEstablishmentType}
                        className="select-control"
                        components={{ DropdownIndicator: PerformerDropdownIndicator }}
                      />
                    </div>

                    {showDashboardDetails && (
                      <div>
                        <div>
                          {filters.establishmentType !== "CLRA" &&
                            hasUserAccess(
                              USER_PRIVILEGES.VIEW_PERFORMER_CLIENT_REGISTER_QUE,
                            ) && (
                              <button className="button-primary"
                                onClick={handleProcessRegisters}
                                disabled={
                                  !filters.company ||
                                  !filters.associateCompany ||
                                  !filters.stateName ||
                                  !filters.locations.length ||
                                  !filters.year ||
                                  !filters.month ||
                                  !filters.establishmentType
                                }
                              >
                                Generate Registers
                              </button>
                            )}
                          {filters.establishmentType === "CLRA" &&
                            hasUserAccess(
                              USER_PRIVILEGES.VIEW_PERFORMER_CLIENT_REGISTER_QUE,
                            ) && (
                              <button className="button-primary"
                                onClick={handleProcessRegisters}
                                disabled={
                                  !filters.company ||
                                  !filters.associateCompany ||
                                  !filters.year ||
                                  !filters.month ||
                                  !filters.establishmentType
                                }
                              >
                                Generate Registers
                              </button>
                            )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={{ display: "flex", gap: "12px" }}>
                    <div className="filter-subcontainer">
                      <label className="filter-label"><small>Company</small></label>
                      <Select
                        options={companyOptions}
                        value={selectedCompany}
                        onChange={handleChangeCompany}
                        isDisabled={!filters.establishmentType}
                        className="select-control"
                        components={{ DropdownIndicator: PerformerDropdownIndicator }}
                      />
                    </div>

                    <div className="filter-subcontainer">
                      <label className="filter-label"><small>Associate Company</small></label>
                      <Select
                        options={associateCompanyOptions}
                        value={selectedAssociateCompany}
                        onChange={handleChangeAssociateCompany}
                        isDisabled={!filters.company}
                        className="select-control"
                        components={{ DropdownIndicator: PerformerDropdownIndicator }}
                      />
                    </div>

                    {filters.establishmentType !== "CLRA" && (
                      <>
                        <div className="filter-subcontainer">
                          <label className="filter-label"><small>State</small></label>
                          <Select
                                options={stateOptions}
                            value={selectedStateName}
                            onChange={handleChangeStateName}
                            isDisabled={!filters.associateCompany}
                            className="select-control"
                            components={{ DropdownIndicator: PerformerDropdownIndicator }}
                          />
                        </div>
                        <div className="filter-subcontainer">
                          <label className="filter-label"><small>Location</small></label>
                          <Select
                                options={nonClraLocationOptions}
                            value={selectedLocationsMulti}
                            onChange={handleChangeLocationMulti}
                            isDisabled={!filters.stateName}
                            isMulti
                            placeholder="All"
                            className="select-control"
                            components={{ DropdownIndicator: PerformerDropdownIndicator }}
                            styles={{
                              multiValueRemove: (base: any) => ({
                                ...base,
                                ':hover': {
                                  backgroundColor: 'transparent',
                                  color: 'inherit',
                                },
                              }),
                            }}
                          />
                        </div>
                      </>
                    )}

                    {filters.establishmentType === "CLRA" && (
                      <>
                        <div className="filter-subcontainer">
                          <label className="filter-label"><small>Company Location</small></label>
                          <Select
                                options={clraLocationOptions}
                            value={selectedLocationSingle}
                            onChange={handleChangeLocationSingle}
                            isDisabled={!filters.associateCompany}
                            placeholder="All"
                            className="select-control"
                            components={{ DropdownIndicator: PerformerDropdownIndicator }}
                          />
                        </div>
                        <div className="filter-subcontainer">
                          <label className="filter-label"><small>Employer</small></label>
                          <Select
                                options={employerOptions}
                            value={selectedEmployer}
                            onChange={handleChangeEmployer}
                            isDisabled={filters.locations.length === 0}
                            className="select-control"
                            components={{ DropdownIndicator: PerformerDropdownIndicator }}
                          />
                        </div>
                        <div className="filter-subcontainer">
                          <label className="filter-label"><small>Employer Location</small></label>
                          <Select
                                options={employerLocationOptions}
                            value={selectedEmployerLocation}
                            onChange={handleChangeEmployerLocation}
                            isDisabled={!filters.employer}
                            className="select-control"
                            components={{ DropdownIndicator: PerformerDropdownIndicator }}
                          />
                        </div>
                      </>
                    )}

                    <div className="filter-subcontainer">
                      <label className="filter-label"><small>Year</small></label>
                      <Select
                        options={yearOptions}
                        value={selectedYear}
                        onChange={handleChangeYear}
                        isDisabled={!filters.locations.length}
                        className="select-control"
                        components={{ DropdownIndicator: PerformerDropdownIndicator }}
                      />
                    </div>

                    <div className="filter-subcontainer">
                      <label className="filter-label"><small>Month</small></label>
                      <Select
                        options={monthOptions}
                        value={selectedMonth}
                        onChange={handleChangeMonth}
                        isDisabled={!filters.year}
                        className="select-control"
                        components={{ DropdownIndicator: PerformerDropdownIndicator }}
                      />
                    </div>

                    {filters.establishmentType === "CLRA" && (
                      <>
                        <div className="d-flex align-items-end">
                          <div className="d-flex flex-column">
                            <button
                              type="submit"
                              className="button-primary"
                              onClick={(e) => {
                                e.preventDefault();
                                if (!filters.employer || !filters.employerLocation) {
                                  return toast.error("Please select Employer and Employer Location");
                                }
                                setOpenMoreFilters(true);
                              }}
                              style={{ width: "136px" }}
                            >
                              <span className="ms-2">More Filters</span>
                            </button>
                          </div>
                        </div>

                        <Modal
                          show={openMoreFilters}
                          onHide={() => setOpenMoreFilters(false)}
                          backdrop="static"
                          animation={false}
                          centered
                          size="lg"
                          className="performer-filters-modal"
                        >
                          <Modal.Header closeButton={true}>
                            <Modal.Title className="bg d-flex align-items-center justify-content-between w-100">
                              <div className="fs-5 fw-semibold">More Filters</div>
                              <div>
                                <IoMdClose
                                  onClick={() => setOpenMoreFilters(false)}
                                  style={{ cursor: "pointer", marginRight: "8px", verticalAlign: "middle" }}
                                />
                              </div>
                            </Modal.Title>
                          </Modal.Header>
                          <Modal.Body>
                            <div className="d-flex justify-content-start">
                              <div className="col-12">
                                <div className="row">
                                  <div className="col-12">
                                    <div className="form-group performer-form-field">
                                      <label className="form-label text-sm">Sub Contractor</label>
                                      <div className="input-group">
                                        <Select
                                          placeholder="Select Sub Contractor"
                                          options={subContractorOptions}
                                          value={selectedSubContractor}
                                          onChange={handleChangeSubContractor}
                                          isDisabled={!filters.employerLocation}
                                          className="select-control w-100"
                                          menuPosition="fixed"
                                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                        />
                                      </div>
                                    </div>
                                    <div className="form-group performer-form-field">
                                      <label className="form-label text-sm">Sub Contractor Location</label>
                                      <div className="input-group">
                                        <Select
                                          placeholder="Select Sub Contractor Location"
                                          options={subContractorLocationOptions}
                                          value={selectedSubContractorLocation}
                                          onChange={handleChangeSubContractorLocation}
                                          isDisabled={!filters.subContractor}
                                          className="select-control w-100"
                                          menuPosition="fixed"
                                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Modal.Body>
                          <Modal.Footer className="d-flex gap-2 flex-nowrap">
                            <button className="button-secondary m-0 w-50" onClick={clearMoreFilters}>
                              Clear All
                            </button>
                            <button className="button-primary m-0 w-50" onClick={applyMoreFilters}>
                              Submit
                            </button>
                          </Modal.Footer>
                        </Modal>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              <div style={{ padding: "0 20px" }}>
                <PerformerTable
                  columns={[
                    { label: "Company", sortKey: "company_id", render: (row) => (row.company && row.company.name) ? row.company.name : "NA" },
                    { label: "AssociateCompany", sortKey: "associate_company_id", render: (row) => (row.associate_company && row.associate_company.name) ? row.associate_company.name : "NA" },
                    { label: "State", render: (row) => (row.state && row.state.name) ? row.state.name : "NA" },
                    { label: "Location", sortKey: "location_id", render: (row) => (row.location && row.location.name) ? row.location.name : "NA" },
                    ...(showCLRAColumns ? [
                      { label: "Employer", render: (row: any) => (row.employer && row.employer.name) ? row.employer.name : "NA" },
                      { label: "Employer Location", render: (row: any) => (row.employer_location && row.employer_location.location_name) ? row.employer_location.location_name : "NA" },
                      { label: "Sub Contractor", render: (row: any) => (row.sub_contractor && row.sub_contractor.name) ? row.sub_contractor.name : "NA" },
                      { label: "Sub Contractor Location", render: (row: any) => (row.sub_contractor_location && row.sub_contractor_location.location_name) ? row.sub_contractor_location.location_name : "NA" },
                    ] : []),
                    { label: "Year", sortKey: "year", render: (row) => row.year || "NA" },
                    { label: "Month", render: (row) => row.month || "NA" },
                    { label: "Que Id", render: (row) => row.que_id || "NA" },
                    { label: "Que Status", render: (row) => row.que_status ? (QUE_STATUS_DISPLAY[row.que_status] || row.que_status) : "NA" },
                    {
                      label: "Download",
                      render: (row: any) => (
                        <div style={{ display: "flex", justifyContent: "center" }}>
                          {row.que_status === "pdf_processing_partially_completed" || row.que_status === "pdf_processing_completed" ? (
                            <Icon style={{ color: "#0654AD", opacity: 1, pointerEvents: "auto" }} type="button" name={"download"} text={"download"} action={() => onclickDownloadBoth(row)} />
                          ) : (
                            <Icon style={{ color: "#0654AD", opacity: 0.5, pointerEvents: "none" }} name={"download"} text={"download"} />
                          )}
                        </div>
                      ),
                    },
                    { label: "Created Date", render: (row) => formatDate(row.created_at) || "NA" },
                    { label: "Last Updated", render: (row) => formatDate(row.completed_date) || "NA" },
                  ]}
                  data={stateRegisterQue || []}
                  rowKey={(row) => row.que_id || row._id}
                  count={stateRegisterQueCount}
                  page={searchConfig.page}
                  rowsPerPage={searchConfig.rowsPerPage}
                  activeSort={searchConfig.activeSort}
                  sortType={searchConfig.sortType}
                  onSort={handleSort}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </div>
            </div>
        </div>
      </div>
    </div>
    {loading && <PageLoader>Loading...</PageLoader>}
    {downloading && <PageLoader>Processing Registers...</PageLoader>}
    </>
  );
};
export default RegistersQueue;
